import React, { useState } from 'react';
import { useTheme } from '../../ThemeContext';

/**
 * Professional feedback modal component
 * Provides a clean interface for users to submit feedback
 * 
 * @param {boolean} visible - Controls modal visibility
 * @param {Function} onClose - Function to close the modal
 * @param {Function} onSubmit - Function to handle feedback submission
 */
const FeedbackModal = ({ 
  visible, 
  onClose, 
  onSubmit 
}) => {
  const { themeMode, currentTheme } = useTheme();
  const [feedbackText, setFeedbackText] = useState('');
  const [validationError, setValidationError] = useState('');

  if (!visible) return null;

  const validateFeedback = (text) => {
    const trimmedText = text.trim();
    if (trimmedText.length === 0) {
      return 'Please enter your feedback message.';
    }
    if (trimmedText.length < 10) {
      return `Message must be at least 10 characters long. Current: ${trimmedText.length} characters.`;
    }
    return '';
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setFeedbackText(newText);
    
    // Clear validation error when user starts typing
    if (validationError && newText.trim().length >= 10) {
      setValidationError('');
    }
  };

  const handleSubmit = () => {
    const error = validateFeedback(feedbackText);
    if (error) {
      setValidationError(error);
      return;
    }
    
    setValidationError('');
    onSubmit(feedbackText.trim());
    setFeedbackText('');
  };

  const handleClose = () => {
    setFeedbackText('');
    setValidationError('');
    onClose();
  };

  // Theme-based styling - consistent with BillingRestrictionPopup
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const modalStyle = {
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    color: currentTheme.homePage.textColor,
    borderRadius: '1rem', // 16px converted to rem
    padding: '0',
    maxWidth: '31.25rem', // 500px converted to rem
    width: '90%',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: themeMode === 'dark' 
      ? '0 1.25rem 2.5rem rgba(0, 0, 0, 0.6), 0 0 0 0.0625rem rgba(255, 255, 255, 0.1)' // 20px, 40px, 1px converted
      : '0 1.25rem 2.5rem rgba(0, 0, 0, 0.15), 0 0 0 0.0625rem rgba(0, 0, 0, 0.05)', // 20px, 40px, 1px converted
    border: themeMode === 'dark' ? '0.0625rem solid #302d4c' : '0.0625rem solid #e9e8ea', // 1px converted to rem
    animation: 'slideIn 0.3s ease-out',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
  };

  const headerStyle = {
    padding: '1.5rem 1.5rem 0 1.5rem', // 24px converted to rem
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: `0.0625rem solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`, // 1px converted to rem
    paddingBottom: '1rem', // 16px converted to rem
    marginBottom: '1.5rem' // 24px converted to rem
  };

  const titleStyle = {
    fontSize: '1.25rem', // 20px converted to rem
    fontWeight: '600',
    color: '#FF6B47',
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem' // 12px converted to rem
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem', // 24px converted to rem
    color: currentTheme.homePage.textColor,
    cursor: 'pointer',
    padding: '0.25rem', // 4px converted to rem
    borderRadius: '0.25rem', // 4px converted to rem
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem', // 32px converted to rem
    height: '2rem' // 32px converted to rem
  };

  const contentStyle = {
    padding: '0 1.5rem', // 24px converted to rem
    marginBottom: '1.5rem' // 24px converted to rem
  };

  const descriptionStyle = {
    fontSize: '0.875rem', // 14px converted to rem
    lineHeight: '1.6',
    color: themeMode === 'dark' ? '#B0B0B0' : '#666666',
    margin: '0 0 1rem 0', // 16px converted to rem
    textAlign: 'left'
  };

  const textareaStyle = {
    width: '100%',
    height: '7.5rem', // 120px converted to rem
    padding: '0.75rem', // 12px converted to rem
    border: validationError 
      ? '0.0625rem solid #ff4d4f' // Red border for error state
      : themeMode === 'dark' ? '0.0625rem solid #302d4c' : '0.0625rem solid #e9e8ea', // 1px converted to rem
    borderRadius: '0.5rem', // 8px converted to rem
    fontSize: '0.875rem', // 14px converted to rem
    fontFamily: 'inherit',
    backgroundColor: themeMode === 'dark' ? '#1a1a2e' : '#ffffff',
    color: currentTheme.homePage.textColor,
    outline: 'none',
    resize: 'vertical',
    minHeight: '5rem', // 80px converted to rem
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box'
  };

  const errorMessageStyle = {
    color: '#ff4d4f',
    fontSize: '0.75rem', // 12px converted to rem
    marginTop: '0.5rem', // 8px converted to rem
    marginBottom: '0',
    lineHeight: '1.4'
  };

  const footerStyle = {
    padding: '0 1.5rem 1.5rem 1.5rem', // 24px converted to rem
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem' // 12px converted to rem
  };

  const buttonBaseStyle = {
    padding: '0.75rem 1.5rem', // 12px 24px converted to rem
    borderRadius: '0.5rem', // 8px converted to rem
    border: 'none',
    fontSize: '0.875rem', // 14px converted to rem
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '5rem', // 80px converted to rem
    height: '2.75rem', // 44px converted to rem
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const primaryButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#FF6B47',
    color: '#ffffff',
    cursor: 'pointer'
  };

  const secondaryButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: themeMode === 'dark' ? '#302d4c' : '#e9e8ea',
    color: currentTheme.homePage.textColor
  };

  const getFeedbackIcon = () => (
    <svg width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM8 14H6v-2h2v2zm0-3H6V9h2v2zm0-3H6V6h2v2zm7 6h-6v-2h6v2zm3-3h-9V9h9v2zm0-3h-9V6h9v2z" fill="#FF6B47"/>
    </svg>
  );

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateY(-1.25rem) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }
        `}
      </style>
      <div style={overlayStyle} onClick={handleClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>
              {getFeedbackIcon()}
              We'd love your feedback
            </h2>
            <button 
              style={closeButtonStyle}
              onClick={handleClose}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.7'}
            >
              ×
            </button>
          </div>
          
          <div style={contentStyle}>
            <p style={descriptionStyle}>
              Let us know what you think or if there's anything we can improve.
            </p>
            <textarea
              value={feedbackText}
              onChange={handleTextChange}
              placeholder="Type here..."
              style={textareaStyle}
              onFocus={(e) => {
                if (!validationError) {
                  e.target.style.borderColor = '#FF6B47';
                }
              }}
              onBlur={(e) => {
                if (!validationError) {
                  e.target.style.borderColor = themeMode === 'dark' ? '#302d4c' : '#e9e8ea';
                }
              }}
            />
            {validationError && (
              <p style={errorMessageStyle}>
                {validationError}
              </p>
            )}
          </div>
          
          <div style={footerStyle}>
            <button
              style={secondaryButtonStyle}
              onClick={handleClose}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = themeMode === 'dark' ? '#404040' : '#d0d0d0';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = themeMode === 'dark' ? '#302d4c' : '#e9e8ea';
              }}
            >
              Cancel
            </button>
            <button
              style={primaryButtonStyle}
              onClick={handleSubmit}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#FF5722';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#FF6B47';
              }}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FeedbackModal; 